import { Box, Center } from '@chakra-ui/react'
import { Loading } from '@components'
import { AudioCaptureProvider } from '@context'
import { useExperienceManager, useWidget } from '@hooks'
import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useSessionControllerPostSession } from '~/clinician-api'
import { SelectMic } from './select-mic'
import { StartRecording } from './start-recording'
import type { SessionSetting } from './types'

const steps = {
  1: SelectMic,
  2: StartRecording
}

export const MiniWidgetStartSession = () => {
  const history = useHistory()
  const { mutateAsync: createSessionDraft } = useSessionControllerPostSession()
  const { isPlusPlanEnabled } = useExperienceManager()
  const [sessionId, setSessionId] = useState<string | undefined>()
  const { settings, selectedClientId, selectClientOptions } = useWidget()
  const [sessionSetting, setSessionSetting] = useState<SessionSetting>(
    (selectClientOptions?.sessionSetting as SessionSetting) ?? 'telehealth'
  )
  const [usingHeadphones, setUsingHeadphones] = useState(false)
  const [currentStep, setCurrentStep] = useState(1)

  useEffect(() => {
    if (selectedClientId) {
      setSessionSetting(selectClientOptions?.sessionSetting as SessionSetting)
      setUsingHeadphones(selectClientOptions?.usingHeadphones ?? false)
      startSession(selectedClientId, selectClientOptions)
    }
  }, [selectedClientId])

  const startSession = async (clientId: string, options: any) => {
    const sessionExternalId = options?.sessionExternalId
    const sessionDraft = await createSessionDraft(
      {
        data: {
          patientId: clientId,
          isExtension: false,
          isChime: false,
          extensionInitiatedFrom: null,
          isDictation: false,
          resumeInProgress: true,
          isAssistEnabled: isPlusPlanEnabled,
          isTelehealth: options?.sessionSetting === 'telehealth',
          isUsingHeadphones: options?.usingHeadphones,
          externalId: sessionExternalId,
          source: 'partner-api'
        }
      },
      {
        onError: () => {
          console.error(
            `Blueprint: selectClient failed. Please ensure client with id ${clientId} is valid.`
          )
        },
        onSuccess: response => {
          // eslint-disable-next-line
          parent.postMessage(
            { type: 'BP_SELECT_CLIENT_COMPLETE', sessionId: response.id },
            '*'
          )

          if (response.inProgress) {
            history.push(
              `/mini-widget/patient/${clientId}/session/${response.id}`
            )
          }
        }
      }
    )
    setSessionId(sessionDraft.id)
  }

  const Step = steps[currentStep as keyof typeof steps]

  return (
    <Box
      id="bp-extension-loaded"
      bg={settings.minifiedViewSettings?.container?.backgroundColor ?? 'white'}
      display="flex"
      flexDirection="column"
      height="100%"
      width="100%"
      p="small"
    >
      {!selectedClientId ? (
        <Center h="100%">
          <Loading
            color={
              settings?.minifiedViewSettings?.audioIndicatorColor ?? 'primary'
            }
          />
        </Center>
      ) : (
        <AudioCaptureProvider sessionId={sessionId}>
          <Step
            sessionSetting={sessionSetting}
            setSessionSetting={setSessionSetting}
            usingHeadphones={usingHeadphones}
            setUsingHeadphones={setUsingHeadphones}
            nextStep={() => setCurrentStep(currentStep + 1)}
            clientId={selectedClientId}
            sessionId={sessionId}
          />
        </AudioCaptureProvider>
      )}
    </Box>
  )
}
